/**
 * Scroll-video duration from the live preview, in export pixels.
 *
 * The file is lead-in blank + credit PNGs + lead-out blank. Each blank is
 * one full frame (createBlankPng, height = videoHeight). Normally playback
 * ends when the lead-out fills the frame. With end hold enabled, the final
 * block becomes pinned when centered while the rest of the strip keeps
 * moving, then the fixed ending completes.
 *
 * Credit height is taken from the inner content box (the element the PNG
 * is captured from), not the wrapper's border box — the wrapper can be
 * wider than that column, which was shortening the estimate.
 *
 * Prefer fpsNum/fpsDen (same rationals as export) over a float fps.
 */

import { getRenderXStep } from "../Preview/scrollEndBehavior";

function contentBox(block) {
  const inner = [...block.children].find(
    (child) =>
      child instanceof HTMLElement &&
      !child.classList.contains("watermark") &&
      !child.classList.contains("watermark_displaynone"),
  );
  return inner || block;
}

function toExportHeight(el, videoWidth) {
  const box = contentBox(el);
  const layoutWidth = Math.max(1, box.offsetWidth);
  const layoutHeight = Math.max(el.offsetHeight, el.scrollHeight, box.offsetHeight);
  return layoutHeight * (videoWidth / layoutWidth);
}

export function getScrollRunningTime({
  videoWidth,
  videoHeight,
  sppf,
  fps,
  fpsNum,
  fpsDen,
  endHoldEnabled = false,
}) {
  const blocks = document.querySelectorAll(".blockWrapper");
  const pxPerFrame = Number(sppf);

  if (!blocks.length || !pxPerFrame || !videoWidth || !videoHeight) {
    return { min: 0, sec: 0 };
  }

  const num = Number(fpsNum);
  const den = Number(fpsDen);
  const frameDuration =
    num > 0 && den > 0 ? num / den : 1 / Math.max(0.0001, Number(fps) || 0);

  if (!frameDuration || !Number.isFinite(frameDuration)) {
    return { min: 0, sec: 0 };
  }

  let cumulativeHeight = 0;
  let previousRoundedHeight = 0;
  const blockHeights = Array.from(blocks).map((el) => {
    cumulativeHeight += toExportHeight(el, videoWidth);
    const nextRoundedHeight = Math.round(cumulativeHeight);
    const height = Math.max(1, nextRoundedHeight - previousRoundedHeight);
    previousRoundedHeight = nextRoundedHeight;
    return height;
  });

  // RenderX consumes each PNG in whole pixel-step frames and does not carry a
  // boundary overshoot into the next image. Mirror that frame domain here so
  // the displayed running time agrees with the exported end hold.
  const step = getRenderXStep(pxPerFrame);
  const imageFrameSpan = (height) =>
    Math.ceil(Math.max(0, Number(height) || 0) / step);
  const leadingBlankFrames = imageFrameSpan(videoHeight);
  const lastHeight = blockHeights[blockHeights.length - 1] || 0;
  const scrollFrames = endHoldEnabled
    ? Math.max(
        0,
        leadingBlankFrames +
          blockHeights
            .slice(0, -1)
            .reduce((sum, height) => sum + imageFrameSpan(height), 0) +
          Math.round((lastHeight - videoHeight) / (2 * step)),
      )
    : leadingBlankFrames +
      blockHeights.reduce(
        (sum, height) => sum + imageFrameSpan(height),
        0,
      );
  const timeSec =
    scrollFrames * frameDuration +
    (endHoldEnabled ? 10 : 0);
  const totalSec = Math.max(0, Math.round(timeSec));

  return {
    min: Math.floor(totalSec / 60),
    sec: totalSec % 60,
  };
}
